/* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role */
/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState, useEffect } from 'react';
import { FaCheckCircle, FaThList, FaClock } from 'react-icons/fa';
import { FiClipboard } from 'react-icons/fi';
import {
  MdArrowDropUp,
  MdArrowDropDown,
  MdSearch,
  MdEdit,
  MdFileDownload,
  MdCloudDownload,
  MdError,
  MdRemoveCircle,
} from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';

import { MenuItem, ListItemIcon, makeStyles } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';
import { Skeleton } from '@material-ui/lab';
import moment from 'moment';
import { useDebounce } from 'use-debounce';

import Menu from '~/components/Menu';
import PageTitle from '~/components/PageTitle';
import Pagination from '~/components/Pagination';
import Table from '~/components/Table';
import TableData from '~/components/Table/TableData';
import { useAuth } from '~/hooks/auth';
import history from '~/services/history';
import { IState } from '~/store';
import {
  getMetadataFileDataListRequest,
  getMetadataTemplateDataListRequest,
  listDataListRequest,
} from '~/store/modules/dataList/actions';
import { ISortBy, ISortOrder } from '~/store/modules/dataList/types';
import { permissions } from '~/types/constants';

import {
  Container,
  Content,
  HeaderContent,
  Link,
  ContainerInputSearch,
  RowData,
  Sortable,
  NoDataListAvailable,
} from './styles';

moment.locale('pt-br');

const useStyles = makeStyles(() => ({
  tooltip: {
    maxWidth: '320px',
    backgroundColor: '#4D4D4D',
  },
  arrow: {
    color: '#4D4D4D',
  },
}));

const DataLists: React.FC = () => {
  const [resetPage, setResetPage] = useState<boolean>(false);
  const [customLimit, setCustomLimit] = useState<number>(15);
  const [search, setSearch] = useState<string>('');

  const [searchDebounce] = useDebounce(search, 800);

  const totalDataLists = useSelector(
    (state: IState) => state.dataList.totalDataLists,
  );
  const dataLists = useSelector((state: IState) => state.dataList.dataLists);
  const sortOrder = useSelector((state: IState) => state.dataList.sortOrder);
  const sortBy = useSelector((state: IState) => state.dataList.sortBy);

  const loading = useSelector((state: IState) => state.dataList.loading);

  const { checkPermission } = useAuth();

  const dispatch = useDispatch();
  const classes = useStyles();

  const loadDataLists = (
    page: number,
    newSortOrder: ISortOrder,
    newSortBy: ISortBy,
  ) => {
    const search = searchDebounce;

    dispatch(
      listDataListRequest(search, page, customLimit, newSortOrder, newSortBy),
    );
  };

  const handleChangePage = (page: number, limit: number) => {
    loadDataLists(page, sortOrder, sortBy);
    setCustomLimit(limit);
  };

  const handleToggleOrder = (newSortBy: ISortBy) => {
    const page = 1;
    let newSortOrder: ISortOrder = 'desc';

    if (newSortBy === sortBy) {
      newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    }

    loadDataLists(page, newSortOrder, newSortBy);
  };

  const handleGoToEditForm = (id: string) => {
    history.push(`/data-lists/${id}`);
  };

  const handleGoToEditData = (id: string) => {
    history.push(`/data-lists-update-data/${id}`);
  };

  const handleDownloadTemplate = () => {
    dispatch(getMetadataTemplateDataListRequest());
  };

  const handleGetFile = (id: string) => {
    dispatch(getMetadataFileDataListRequest(id));
  };

  const getStatusIcon = (status: any) => {
    switch (status) {
      case 'PENDING':
        return (
          <Tooltip arrow title="Pendente" classes={classes}>
            <div style={{ display: 'inline' }}>
              <FaClock
                size={16}
                style={{ cursor: 'default', color: '#f1c40f' }}
              />
            </div>
          </Tooltip>
        );
      case 'IN PROGRESS':
        return (
          <Tooltip arrow title="Em andamento" classes={classes}>
            <div style={{ display: 'inline' }}>
              <FaClock
                size={16}
                style={{ cursor: 'default', color: '#3498db' }}
              />
            </div>
          </Tooltip>
        );
      case 'FINISHED':
        return (
          <Tooltip arrow title="Concluído" classes={classes}>
            <div style={{ display: 'inline' }}>
              <FaCheckCircle
                size={16}
                style={{ cursor: 'default', color: '#07bc0c' }}
              />
            </div>
          </Tooltip>
        );
      case 'ERROR':
        return (
          <Tooltip arrow title="Erro" classes={classes}>
            <div style={{ display: 'inline' }}>
              <MdError
                size={16}
                style={{ cursor: 'default', color: '#EB0045' }}
              />
            </div>
          </Tooltip>
        );
      default:
        return (
          <Tooltip arrow title="Não iniciado" classes={classes}>
            <div style={{ display: 'inline' }}>
              <MdRemoveCircle
                size={16}
                style={{ cursor: 'default', color: '#000000' }}
              />
            </div>
          </Tooltip>
        );
    }
  };

  useEffect(() => {
    const page = 1;

    loadDataLists(page, sortOrder, sortBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDebounce, customLimit]);

  return (
    <Container>
      <PageTitle>Cadastros</PageTitle>

      <HeaderContent>
        <HeaderContent>
          {checkPermission([permissions.CREATE_DATA_LIST]) && (
            <Link to="/data-lists/new">Novo Cadastro</Link>
          )}
          <ContainerInputSearch>
            <MdSearch size={25} />
            <input
              aria-label="search-input"
              id="search-input"
              type="text"
              placeholder="Buscar cadastro"
              onChange={(e) => setSearch(e.target.value)}
            />
          </ContainerInputSearch>
        </HeaderContent>
      </HeaderContent>

      <Content>
        <Table>
          <thead>
            <tr>
              <th />
              <th style={{ width: '15%' }}>
                <Sortable
                  type="button"
                  onClick={() => handleToggleOrder('updated_at')}
                >
                  Última atualização
                  {sortBy === 'updated_at' && sortOrder === 'asc' ? (
                    <MdArrowDropUp size={20} />
                  ) : (
                    <MdArrowDropDown size={20} />
                  )}
                </Sortable>
              </th>
              <th style={{ width: '20%' }}>
                <Sortable
                  type="button"
                  onClick={() => handleToggleOrder('name')}
                >
                  Nome do Cadastro
                  {sortBy === 'name' && sortOrder === 'asc' ? (
                    <MdArrowDropUp size={20} />
                  ) : (
                    <MdArrowDropDown size={20} />
                  )}
                </Sortable>
              </th>
              <th style={{ width: '20%' }}>
                <Sortable
                  type="button"
                  onClick={() => handleToggleOrder('form_field_id')}
                >
                  ID do Formulário
                  {sortBy === 'form_field_id' && sortOrder === 'asc' ? (
                    <MdArrowDropUp size={20} />
                  ) : (
                    <MdArrowDropDown size={20} />
                  )}
                </Sortable>
              </th>
              <th style={{ width: '20%' }}>Criado por</th>
              <th style={{ width: '20%' }}>Atualizado por</th>
              <th style={{ width: '20%' }}>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {!loading && !dataLists?.length ? (
              <tr>
                <td colSpan={8}>
                  <NoDataListAvailable>
                    <div>
                      <FiClipboard size="80" color="#6c6c6c" />
                    </div>
                    <p>Nenhum cadastro existente</p>
                    <span>
                      O sistema ainda não possui nenhum cadastro existente.
                      <br />
                      Contate o administrador.
                    </span>
                  </NoDataListAvailable>
                </td>
              </tr>
            ) : !loading && dataLists ? (
              dataLists.map((dataList) => (
                // eslint-disable-next-line react/jsx-indent
                <>
                  <RowData key={dataList.id}>
                    <td />
                    <TableData>
                      {moment(dataList.updated_at).format('DD/MM/YYYY HH:mm')}
                    </TableData>
                    <TableData>{dataList.name}</TableData>
                    <TableData>{dataList.form_field_id}</TableData>
                    <TableData>{dataList.created_by}</TableData>
                    <TableData>{dataList.updated_by}</TableData>
                    <TableData>{getStatusIcon(dataList.status)}</TableData>

                    <td>
                      <Menu>
                        {checkPermission([permissions.UPDATE_DATA_LIST]) && (
                          <MenuItem
                            disabled={loading}
                            onClick={() => handleGoToEditForm(dataList.id)}
                          >
                            <ListItemIcon>
                              <MdEdit size={16} /> Editar cadastro
                            </ListItemIcon>
                          </MenuItem>
                        )}
                        {checkPermission([permissions.UPDATE_DATA_LIST]) && (
                          <MenuItem
                            disabled={loading}
                            onClick={() => handleGoToEditData(dataList.id)}
                          >
                            <ListItemIcon>
                              <FaThList size={16} /> Editar dados
                            </ListItemIcon>
                          </MenuItem>
                        )}
                        {checkPermission([permissions.UPDATE_DATA_LIST]) && (
                          <MenuItem
                            disabled={loading}
                            onClick={handleDownloadTemplate}
                          >
                            <ListItemIcon>
                              <MdFileDownload size={16} /> Baixar modelo
                            </ListItemIcon>
                          </MenuItem>
                        )}
                        {checkPermission([permissions.VIEW_ALL_DATA_LISTS]) && (
                          <MenuItem
                            disabled={loading}
                            onClick={() => handleGetFile(dataList.id)}
                          >
                            <ListItemIcon>
                              <MdCloudDownload size={16} /> Baixar dados
                            </ListItemIcon>
                          </MenuItem>
                        )}
                      </Menu>
                    </td>
                  </RowData>
                </>
              ))
            ) : (
              Array.from(Array(5)).map((_value, index) => (
                // eslint-disable-next-line react/jsx-indent
                <RowData key={`skeleton-${index}`}>
                  <td />
                  <td>
                    <Skeleton animation="wave" height={40} width="90%" />
                  </td>
                  <td>
                    <Skeleton animation="wave" height={40} width="90%" />
                  </td>
                  <td>
                    <Skeleton animation="wave" height={40} width="90%" />
                  </td>
                  <td>
                    <Skeleton animation="wave" height={40} width="90%" />
                  </td>
                  <td>
                    <Skeleton animation="wave" height={40} width="90%" />
                  </td>
                  <td>
                    <Skeleton animation="wave" height={40} width="90%" />
                  </td>
                  <td />
                </RowData>
              ))
            )}
          </tbody>
        </Table>
        <Pagination
          total={totalDataLists}
          onChange={(page: number, limit: number) =>
            handleChangePage(page, limit)
          }
          resetPage={resetPage}
          onReset={(reset) => setResetPage(reset)}
        />
      </Content>
    </Container>
  );
};

export default DataLists;
